import { defineAsyncComponent, type AsyncComponentLoader, type Component } from 'vue'

import type { SoftwareCode, UserType } from './stores/session'

/**
 * The global widget slot (wp/shell OPEN-16 default; S-D1): a mount point outside the routes, rendered by `App.vue`
 * through `src/app/layouts/components/GlobalWidgets.vue` on every page — signed out and full-layout pages included —
 * for what a module shows whatever the route (the assistant's pill and panel, src/modules/assistant; the AI mode's
 * chat, src/modules/ai-mode). A module contributes widgets from `src/modules/<name>/widgets.ts` (default export `GlobalWidget[]`, found by
 * `import.meta.glob` like `routes.ts`); adding one touches no shared file. The registry is the settings tabs' pattern
 * (D-14): a typed definition here, the module owns its components.
 */

/** What a widget's `when` reads; recomputed when the session or the software changes. */
export interface GlobalWidgetContext {
  /** A session is stored. */
  signedIn: boolean
  userType: UserType | null
  /** The session's current software (`re` … `ac`, `ca`, `em`). */
  software: SoftwareCode | null
  /** `VITE_DEV_MODE`: a widget for dev builds only. */
  devMode: boolean
  /** The current route path. */
  path: string
  /** The current route's frame (`meta.layout`; vertical when absent): AI mode (docs/ai-mode) has its own chat. */
  layout: 'vertical' | 'full' | 'ai'
}

export interface GlobalWidget {
  /** Unique across modules (`assistant`); the render key. */
  id: string
  /**
   * The widget's loader — `() => import('./components/AssistantWidget.vue')`. `widgets.ts` is imported eagerly with the
   * app; the loader keeps the widget's code out of the eager bundle until `when` first lets it render.
   */
  load: AsyncComponentLoader
  /** Render only when this answers `true` (default: always). */
  when?: (context: GlobalWidgetContext) => boolean
}

/** A collected widget: its async component and the module that declared it. */
export interface RegisteredWidget {
  id: string
  moduleId: string
  component: Component
  when: (context: GlobalWidgetContext) => boolean
}

export type WidgetModules = Record<string, { default?: unknown }>

const discovered = import.meta.glob<{ default?: unknown }>('/src/modules/*/widgets.ts', { eager: true })

const always = (): boolean => true

function moduleOf(file: string): string {
  return /\/modules\/([^/]+)\//.exec(file)?.[1] ?? file
}

/**
 * Every module's widgets in folder order. Refuses (throws) a definition without an `id` or a `load` function, and an
 * `id` declared twice — at boot, so the mistake shows in every test run.
 */
export function collectWidgets(modules: WidgetModules = discovered): RegisteredWidget[] {
  const seen = new Map<string, string>()
  const widgets: RegisteredWidget[] = []
  for (const file of Object.keys(modules).sort()) {
    const list = modules[file]?.default
    if (list === undefined) continue
    if (!Array.isArray(list)) throw new Error(`${file}: the default export must be an array of GlobalWidget`)
    const moduleId = moduleOf(file)
    for (const entry of list as GlobalWidget[]) {
      if (!entry || typeof entry.id !== 'string' || !entry.id || typeof entry.load !== 'function') {
        throw new Error(`${file}: a widget needs an id and a load function`)
      }
      const owner = seen.get(entry.id)
      if (owner) throw new Error(`Global widget "${entry.id}" is declared by ${owner} and ${moduleId}`)
      seen.set(entry.id, moduleId)
      widgets.push({
        id: entry.id,
        moduleId,
        component: defineAsyncComponent(entry.load),
        when: entry.when ?? always,
      })
    }
  }
  return widgets
}
